// @flow
import * as React from 'react';
import Radium from 'radium';
import { connect } from 'react-redux';
import { compose, withState } from 'recompose';
import moment from 'moment';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import DateTimeField from 'react-datetime';
import resizeImage from 'browser-image-resizer';

import TextEditModal from 'components/TextEditModal';

import { loadAppointments, editAppointment } from 'actions/appointments';
import { addImage, deleteImage } from 'actions/images';

import { Colors, Spacing, Sizes } from 'utils/styleguide';
import ENV from 'environment';

const IMAGE_WIDTH = 256;
const IMAGE_HEIGHT = 144;
const EDIT_FIELD_MAPPING = {
  title: 'Titel',
  description: 'Beschreibung',
};

type EditField = 'title' | 'description';
type Props = {
  appointmentId: string,
  title: string,
  description: string,
  image: ?string,
  dates: Array<number>,
  createdAt: number,
  onDeleteAppointment: () => void,

  // recompose
  showConfirmation: boolean,
  onShowConfirmationChange: (showConfirmation: boolean) => void,
  editField: ?EditField,
  onEditFieldChange: (editField: ?EditField) => void,

  // redux
  onLoadAppointments: typeof loadAppointments,
  onEditAppointment: (field: string, value: string) => void,
  onAddImage: (image: string) => void,
  onDeleteImage: () => void,
};

class Appointment extends React.PureComponent<Props> {
  _fileupload: ?HTMLInputElement;

  _openFileDrawer = (e) => {
    if (!this._fileupload) {
      return;
    }
    this._fileupload.click();
  };

  _onChangeFileData = async ({ nativeEvent: { target } }) => {
    const { onAddImage, onLoadAppointments } = this.props;
    const file = target.files[0];
    if (file && file.type.startsWith('image/')) {
      const resizedFile = await resizeImage(file, {
        quality: 1,
        maxWidth: 1024,
        maxHeight: 768,
      });
      const reader = new FileReader();
      // eslint-disable-next-line
      reader.onloadend = async (e) => {
        await onAddImage(e.target.result);
        await onLoadAppointments();
      };
      reader.readAsDataURL(resizedFile);
    }
  };

  _clearImage = async (e) => {
    e.preventDefault();

    const { onDeleteImage, onLoadAppointments } = this.props;
    await onDeleteImage();
    await onLoadAppointments();
  };

  _hideModal = () => {
    this.props.onShowConfirmationChange(false);
  };

  _onSubmitEdit = async (field, value) => {
    const { onEditAppointment, onLoadAppointments } = this.props;
    await onEditAppointment(field, value);
    await onLoadAppointments();
  };

  render() {
    const {
      appointmentId,
      title,
      description,
      image,
      dates,
      showConfirmation,
      onShowConfirmationChange,
      onDeleteAppointment,
      editField,
      onEditFieldChange,
    } = this.props;

    return (
      <div style={styles.root}>
        <div style={styles.content}>
          <div
            style={[styles.imageWrapper, !image && styles.imageWrapperMissing]}
            onClick={() => !image && this._openFileDrawer()}
          >
            {!image ? (
              <div style={styles.center}>
                <span>Kein Bild verfügbar</span>
              </div>
            ) : (
              <img
                width={IMAGE_WIDTH}
                src={`${ENV.HOST}/images/${image}`}
                alt=""
              />
            )}
            <input
              ref={(ref) => (this._fileupload = ref)}
              type="file"
              style={{ display: 'none' }}
              onChange={this._onChangeFileData}
            />
            {!!image && (
              <div style={styles.clear} onClick={this._clearImage}>
                <span>&#x2A09;</span>
              </div>
            )}
          </div>

          <div style={styles.meta}>
            <div style={styles.title}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{title}</span>
                <Button
                  color="link"
                  size="sm"
                  onClick={() => onEditFieldChange('title')}
                >
                  (bearbeiten)
                </Button>
              </div>
              <Button
                color="danger"
                size="xs"
                onClick={() => onShowConfirmationChange(true)}
              >
                Löschen
              </Button>
            </div>
            <div style={styles.description}>
              {description}
              <Button
                color="link"
                size="sm"
                onClick={() => onEditFieldChange('description')}
              >
                (bearbeiten)
              </Button>
            </div>
            <div style={styles.datesLabel}>Termine:</div>
            <ul style={styles.datesList}>
              {dates.map((d, index) => (
                <li key={`date-${d}`}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DateTimeField
                      defaultValue={d}
                      renderInput={(props, openCalendar) => {
                        return (
                          <span
                            onClick={openCalendar}
                            style={{ cursor: 'pointer' }}
                          >
                            {moment(d).format('DD. MMMM YYYY - HH:mm')}
                          </span>
                        );
                      }}
                      onBlur={(moment) => {
                        const newDates = [...dates];
                        // eslint-disable-next-line
                        newDates[index] = moment.unix() * 1000;
                        this._onSubmitEdit('dates', newDates);
                      }}
                    />
                    <span
                      key={`date-${appointmentId}-${d}-${index}-delete`}
                      style={styles.deleteDate}
                      onClick={() => {
                        const newDates = [
                          ...dates.slice(0, index),
                          ...dates.slice(index + 1),
                        ];
                        this._onSubmitEdit('dates', newDates);
                      }}
                    >
                      (löschen)
                    </span>
                  </div>
                </li>
              ))}
              <li>
                <span
                  style={styles.newDate}
                  onClick={() => {
                    const newDates = [
                      ...dates,
                      dates.length === 0
                        ? +new Date()
                        : dates[dates.length - 1] + 1,
                    ];
                    this._onSubmitEdit('dates', newDates);
                  }}
                >
                  weiteren Termin hinzufügen
                </span>
              </li>
            </ul>
          </div>
        </div>
        {!!editField && (
          <TextEditModal
            title={`${EDIT_FIELD_MAPPING[editField]} bearbeiten`}
            inputType={editField === 'title' ? 'textfield' : 'textarea'}
            initialValue={editField === 'title' ? title : description}
            onCancel={() => onEditFieldChange(null)}
            onSubmit={(value) => {
              this._onSubmitEdit(editField, value);
              onEditFieldChange(null);
            }}
          />
        )}
        <Modal
          size="zs"
          isOpen={showConfirmation}
          toggle={() => onShowConfirmationChange(!showConfirmation)}
        >
          <ModalHeader>Löschen?</ModalHeader>
          <ModalBody>
            Sind Sie sicher, dass Sie diesen Termin unwiderruflich löschen
            wollen?
          </ModalBody>
          <ModalFooter>
            <Button onClick={this._hideModal}>Abbrechen</Button>{' '}
            <Button
              color="danger"
              onClick={() => {
                this._hideModal();
                onDeleteAppointment();
              }}
            >
              Löschen
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

const styles = {
  root: {
    backgroundColor: Colors.White,
    marginBottom: Spacing.Micro,
    padding: Spacing.Micro,
    borderWidth: 1,
    borderColor: Colors.DarkGrey,
    borderStyle: 'solid',
  },
  content: {
    display: 'flex',
  },
  center: {
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageWrapper: {
    position: 'relative',
    width: IMAGE_WIDTH,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: 'pointer',
  },
  clear: {
    borderRadius: '50%',
    backgroundColor: Colors.Black,
    color: Colors.White,
    fontWeight: 'bold',
    fontSize: 12,
    lineHeight: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: Sizes.Micro,
    height: Sizes.Micro,
    position: 'absolute',
    top: -Sizes.Micro / 2,
    right: -Sizes.Micro / 2,
    cursor: 'pointer',
  },
  imageWrapperMissing: {
    height: IMAGE_HEIGHT,
    backgroundColor: Colors.DarkGrey,
    borderWidth: 2,
    borderColor: Colors.LightGrey,
    borderStyle: 'dashed',
  },
  meta: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: Spacing.Micro,
    flexShrink: 1,
    flexGrow: 1,
    marginRight: Spacing.Micro,
  },
  title: {
    display: 'flex',
    fontSize: 18,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    marginTop: Spacing.Micro,
    whiteSpace: 'pre-wrap',
  },
  datesLabel: {
    fontWeight: 'bold',
    marginTop: Spacing.Micro,
  },
  datesList: {
    paddingLeft: Spacing.Small,
  },
  deleteDate: {
    color: Colors.Primary,
    marginLeft: Spacing.Nano,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  newDate: {
    color: '#20a8d8',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      color: '#167495',
    },
  },
};

const mapDispatchToProps = (dispatch, { appointmentId }) => ({
  onLoadAppointments: () => dispatch(loadAppointments()),
  onEditAppointment: (field, value) =>
    dispatch(editAppointment(appointmentId, field, value)),
  onAddImage: (image) =>
    dispatch(addImage('appointment', appointmentId, image)),
  onDeleteImage: () => dispatch(deleteImage('appointment', appointmentId, -1)),
});

export default compose(
  connect(null, mapDispatchToProps),
  withState('showConfirmation', 'onShowConfirmationChange', false),
  withState('editField', 'onEditFieldChange', null),
  Radium,
)(Appointment);



// WEBPACK FOOTER //
// ./src/components/Appointment/Appointment.js